import { useState, useMemo, useCallback, useEffect } from "react";
import { countBlanks, isAllBlanksFilled } from "../../../utils/quiz-parser.js";
function useDragTextQuiz({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  onAnswerChange
}) {
  const [startTime] = useState(() => Date.now());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isShowingCorrectAnswers, setIsShowingCorrectAnswers] = useState(false);
  const blankCount = useMemo(() => countBlanks(quiz.problem), [quiz.problem]);
  const problemKey = useMemo(
    () => `${quizId}-${blankCount}-${quiz.problem.length}`,
    [quizId, blankCount, quiz.problem]
  );
  const isSolved = solveResult !== null && solveResult !== void 0;
  const shouldShowCorrectAnswers = showReallyTrue || isShowingCorrectAnswers;
  const availableOptions = useMemo(() => {
    var _a;
    if (quiz.choices && Array.isArray(quiz.choices) && quiz.choices.length > 0) {
      return quiz.choices.filter((c) => Boolean(c) && typeof c.key === "string" && typeof c.answer === "string").map((c) => ({ key: c.key, label: c.answer }));
    }
    if (((_a = quiz.pairs) == null ? void 0 : _a.right) && Array.isArray(quiz.pairs.right) && quiz.pairs.right.length > 0) {
      return quiz.pairs.right.filter((c) => Boolean(c) && typeof c.key === "string" && typeof c.answer === "string").map((c) => ({ key: c.key, label: c.answer }));
    }
    if (quiz.answers && Array.isArray(quiz.answers) && quiz.answers.length > 0) {
      return quiz.answers.filter((a) => typeof a === "string").map((a) => ({ key: a, label: a }));
    }
    return [];
  }, [quiz.answers, quiz.choices, quiz.pairs]);
  const optionLabelByKey = useMemo(() => {
    const map = /* @__PURE__ */ new Map();
    availableOptions.forEach((o) => map.set(o.key, o.label));
    return map;
  }, [availableOptions]);
  const optionKeyByLabel = useMemo(() => {
    const map = /* @__PURE__ */ new Map();
    availableOptions.forEach((o) => {
      if (!map.has(o.label)) map.set(o.label, o.key);
    });
    return map;
  }, [availableOptions]);
  const normalizeValueToKey = useCallback((value) => {
    if (!value) return "";
    if (optionLabelByKey.has(value)) return value;
    return optionKeyByLabel.get(value) ?? value;
  }, [optionKeyByLabel, optionLabelByKey]);
  const resolveLabel = useCallback((value) => {
    if (!value) return "";
    return optionLabelByKey.get(value) ?? value;
  }, [optionLabelByKey]);
  const computedInitialAnswers = useMemo(() => {
    const base = initialAnswersProp && initialAnswersProp.length > 0 ? initialAnswersProp : isViewSolved && (solveResult == null ? void 0 : solveResult.correctAnswers) ? solveResult.correctAnswers : new Array(blankCount).fill("");
    const normalized = new Array(blankCount).fill("").map((_, idx) => normalizeValueToKey(base[idx] ?? ""));
    return normalized;
  }, [blankCount, isViewSolved, solveResult == null ? void 0 : solveResult.correctAnswers, initialAnswersProp, normalizeValueToKey]);
  const [answers, setAnswers] = useState(computedInitialAnswers);
  const canSubmit = isAllBlanksFilled(answers);
  useEffect(() => {
    setAnswers(computedInitialAnswers);
  }, [computedInitialAnswers]);
  useEffect(() => {
    onAnswerChange == null ? void 0 : onAnswerChange(answers);
  }, [answers, onAnswerChange]);
  const handleBlankClick = useCallback((index) => {
    if (isSolved || isViewSolved) return;
    setCurrentIndex(index);
  }, [isSolved, isViewSolved]);
  const handleClearBlank = useCallback((index) => {
    if (isSolved || isViewSolved) return;
    setAnswers((prev) => {
      const newAnswers = [...prev];
      newAnswers[index] = "";
      return newAnswers;
    });
  }, [isSolved, isViewSolved]);
  const handleSelectOption = useCallback((optionKey) => {
    if (isSolved || isViewSolved) return;
    setAnswers((prev) => {
      const newAnswers = [...prev];
      newAnswers[currentIndex] = optionKey;
      const nextEmptyIndex = newAnswers.findIndex((a, i) => i > currentIndex && a === "");
      if (nextEmptyIndex !== -1) {
        setCurrentIndex(nextEmptyIndex);
      } else {
        const firstEmptyIndex = newAnswers.findIndex((a, i) => i !== currentIndex && a === "");
        if (firstEmptyIndex !== -1) {
          setCurrentIndex(firstEmptyIndex);
        }
      }
      return newAnswers;
    });
  }, [currentIndex, isSolved, isViewSolved]);
  const handleSubmit = useCallback(() => {
    if (!canSubmit || isSolved) return;
    const elapsedTime = Date.now() - startTime;
    onSolve == null ? void 0 : onSolve(quizId, answers, quiz.vars, elapsedTime);
  }, [answers, canSubmit, isSolved, onSolve, quizId, quiz.vars, startTime]);
  return {
    answers,
    currentIndex,
    blankCount,
    problemKey,
    availableOptions,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    resolveLabel,
    handleBlankClick,
    handleClearBlank,
    handleSelectOption,
    handleSubmit,
    quiz,
    quizId,
    solveResult,
    isViewSolved
  };
}
export {
  useDragTextQuiz
};
//# sourceMappingURL=use-drag-text-quiz.js.map
